import clsx from 'clsx'
import CheckoutInputError from 'features/checkout/components/CheckoutInputError/CheckoutInputError'
import {
  DetailedHTMLProps,
  FC,
  FocusEvent,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  FieldValues,
  Validate,
  ValidationRule,
  useFormContext,
} from 'react-hook-form'
import { AiFillEye, AiFillEyeInvisible } from 'react-icons/ai'
import s from './InputV2.module.scss'

export type InputV2Props = {
  id: string
  className?: string
  placeholder: string
  required?: boolean
  errorMessage?: string
  type?: string
  textarea?: boolean
  select?: boolean
  supportingText?: string | ReactNode
  instantFloatingLabel?: boolean
  pattern?: ValidationRule<RegExp> | any
  validate?:
    | Validate<any, FieldValues>
    | Record<string, Validate<any, FieldValues>>
  onBlur?: (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> &
  DetailedHTMLProps<
    InputHTMLAttributes<HTMLTextAreaElement>,
    HTMLTextAreaElement
  >

const InputV2: FC<InputV2Props> = (
  {
    className,
    id,
    placeholder,
    required,
    errorMessage,
    type = 'text',
    textarea,
    select,
    supportingText,
    instantFloatingLabel,
    pattern,
    validate,
    onBlur,
    children,
    ...props
  },
  ref,
) => {
  const {
    register,
    setValue,
    formState: { errors },
    control,
    watch,
    getValues,
  } = useFormContext()

  const inputRef = useRef<HTMLInputElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const selectRef = useRef<HTMLSelectElement | null>(null)

  const handleFloatingLabel = (
    e: FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
      Element
    >,
  ) => {
    if (e.target.parentElement.parentElement.parentElement) {
      if ('' !== e.target.value || e.type === 'focus') {
        e.target.parentElement.parentElement.parentElement.classList.add(
          s.floatingLabel,
        )
      } else {
        e.target.parentElement.parentElement.parentElement.classList.remove(
          s.floatingLabel,
          s.instant,
        )
      }
    }
  }

  // Watch for value changes from setValue and apply floatingLabel if there is a value
  const fieldValue = watch(id) // watching the field value

  useEffect(() => {
    if ((inputRef.current || textareaRef.current) && fieldValue) {
      const refElement = inputRef.current ?? textareaRef.current
      refElement?.parentElement?.parentElement?.parentElement?.classList.add(
        s.floatingLabel,
        s.instant,
      )
    }
  }, [fieldValue]) // Re-run the effect whenever the fieldValue changes

  useEffect(() => {
    const InputV2El = document.querySelector(
      `#${id}`,
    ) as HTMLInputElement | null
    if (InputV2El && InputV2El.value) {
      //check if the InputV2El has a value
      InputV2El.parentElement.parentElement.parentElement.classList.add(
        s.floatingLabel,
      )
    }
  }, [])

  const validation = {
    ...validate,
    pattern,
    required: required || errorMessage,
    onBlur: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (select) return
      setValue(id, e.target.value?.trimEnd(), {
        shouldValidate: true,
      })
      handleFloatingLabel(e)
      onBlur && onBlur(e)
    },
    onChange: (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      handleFloatingLabel(
        e as unknown as FocusEvent<
          HTMLInputElement | HTMLTextAreaElement,
          HTMLSelectElement
        >,
      )
      setValue(id, e.target.value.trimStart(), {
        shouldValidate: false,
      })
    },
  }

  //handleSetShowPassword
  const handleSetShowPassword = () => {
    setShowPassword(!showPassword)
    const input = document.getElementById(id) as HTMLInputElement
    if (input) {
      input.type = showPassword ? 'password' : 'text'
    }
  }

  const [showPassword, setShowPassword] = useState(false)

  return (
    <div
      className={clsx(className, s.inputWrapper, {
        [clsx(s.floatingLabel, s.instant)]: instantFloatingLabel || select,
      })}
      {...props}
    >
      <label htmlFor={id} className={s.label}>
        <span className={s.placeholder}>
          {placeholder} {placeholder && required && '*'}
        </span>
        <span className={s.formInputWrapper}>
          {select ? (
            <select
              className={clsx(s.formInput)}
              id={id}
              {...register(id, validation)}
              ref={selectRef}
            >
              {children}
            </select>
          ) : textarea ? (
            <textarea
              rows={6}
              className={clsx(s.formInput)}
              id={id}
              {...props}
              onFocus={handleFloatingLabel}
              {...register(id, validation)}
              ref={textareaRef}
            ></textarea>
          ) : (
            <div className={'relative'}>
              {/* <Controller
                control={control}
                name={id}
                render={({ field: { ref, value, onChange } }) =>
                  <>
                    <input
                      className={s.formInput}
                      id={id}
                      type={type}
                      onFocus={handleFloatingLabel}
                      onBlur={handleFloatingLabel}
                      aria-invalid={errors[id] ? 'true' : 'false'}
                      onChange={onChange}
                      value={value}
                      {...props}
                      ref={mergeRefs([ref, inputRef])}
                      {...register(id, validation)}
                    />
                  </>
                }
              /> */}

              <input
                className={s.formInput}
                id={id}
                type={type}
                onFocus={handleFloatingLabel}
                aria-invalid={errors[id] ? 'true' : 'false'}
                {...register(id, validation)}
                {...props}
                ref={inputRef}
              />

              {type === 'password' && (
                <div
                  className={s.passwordToggle}
                  onClick={handleSetShowPassword}
                >
                  {showPassword ? <AiFillEyeInvisible /> : <AiFillEye />}
                </div>
              )}
            </div>
          )}
        </span>
      </label>
      <div className={s.supportingContainer}>
        <div>
          {errors[id] && (
            <CheckoutInputError message={errors[id].message as string} />
          )}
        </div>
        {supportingText && <p className={s.supportingText}>{supportingText}</p>}
      </div>
    </div>
  )
}

InputV2.displayName = 'InputV2'
export default InputV2
